import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  Mail,
  MapPin,
  GraduationCap,
  Building2,
  Users,
  Phone,
  Facebook,
  Linkedin,
  Instagram
} from 'lucide-react';
import logo from '../../assets/logo.png';
import { trainingCatalogService, TrainingStats } from '../../services/training-catalog.service';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [stats, setStats] = useState<TrainingStats | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await trainingCatalogService.getStats();
        setStats(data);
      } catch (error) {
        console.error('Failed to fetch footer stats', error);
      }
    };

    fetchStats();
  }, []);

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">

          {/* Brand Section with Stats */}
          <div>
            <div className="flex items-center mb-4">
              <img src={logo} alt="PHINLEX" className="h-8 w-auto" />
            </div>

            <p className="text-gray-300 mb-6 text-sm leading-relaxed">
              {t('public.footer.description')}
            </p>
            <p className="text-gray-300 mb-6 text-sm leading-relaxed">
              <Trans i18nKey="public.footer.description2">
                Solution développée par <a href="https://smartpillsolutions.com/" target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:text-brand-primary/80 transition-colors">SmartPillsolutions</a>
              </Trans>
            </p>

            {/* Stats */}

          </div>

          {/* Contact Section */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">
              {t('public.footer.contact.title')}
            </h3>
            <div className="space-y-3">
              <div className="flex items-center text-sm text-gray-300">
                <Phone className="h-4 w-4 mr-3 text-brand-primary flex-shrink-0" />
                +213 542 47 80 21
              </div>
              <div className="flex items-center text-sm text-gray-300">
                <Mail className="h-4 w-4 mr-3 text-brand-primary flex-shrink-0" />
                info@smartpillsolutions.com
              </div>
              <div className="flex items-center text-sm text-gray-300">
                <MapPin className="h-4 w-4 mr-3 text-brand-primary flex-shrink-0" />
                Alger, Algérie
              </div>
            </div>

            {/* Social Media Links */}
            <div className="mt-6 flex items-center space-x-4">
              <a href="https://www.linkedin.com/showcase/phinlex" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-brand-primary transition-colors" aria-label="LinkedIn">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="https://www.facebook.com/pilule.dz/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-brand-primary transition-colors" aria-label="Facebook">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://www.instagram.com/phinlex_platform?igsh=Z29paTN5cGd1MDZk" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-brand-primary transition-colors" aria-label="Instagram">
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Menu Section */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">
              {t('public.footer.menu.title')}
            </h3>
            <div className="space-y-3">
              <Link
                to="/trainings"
                className="block text-gray-300 hover:text-brand-primary text-sm transition-colors duration-200"
              >
                {t('public.footer.menu.catalog')}
              </Link>
              <Link
                to="/centers"
                className="block text-gray-300 hover:text-brand-primary text-sm transition-colors duration-200"
              >
                {t('public.footer.menu.trainingCenter')}
              </Link>
              <Link
                to="/consultants"
                className="block text-gray-300 hover:text-brand-primary text-sm transition-colors duration-200"
              >
                {t('public.footer.menu.consultant')}
              </Link>
            </div>
          </div>

          {/* Useful Links Section */}
          <div className="flex flex-col h-full">
            <div>
              <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">
                {t('public.footer.usefulLinks')}
              </h3>
              <div className="space-y-3 mb-8">
                <Link
                  to="/faq"
                  className="block text-gray-300 hover:text-brand-primary text-sm transition-colors duration-200"
                >
                  {t('public.footer.faq')}
                </Link>
                <Link
                  to="#"
                  className="block text-gray-300 hover:text-brand-primary text-sm transition-colors duration-200"
                >
                  {t('public.footer.privacyPolicy')}
                </Link>
              </div>
            </div>

            {/* Stats Block - Pushed to bottom of column if needed */}
            <div className="mt-auto pt-6 border-t border-gray-800">
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <div className="text-center group">
                  <div className="flex items-center justify-center w-8 h-8 bg-brand-primary/20 rounded-lg mb-2 mx-auto transition-transform duration-300 group-hover:scale-110 group-hover:bg-brand-primary/30">
                    <GraduationCap className="h-4 w-4 text-brand-primary" />
                  </div>
                  <div className="text-sm font-bold text-white">{stats ? `${stats.totalTrainings}+` : '150+'}</div>
                  <div className="text-[10px] sm:text-xs text-gray-400 mt-1 line-clamp-1" title={t('public.footer.stats.trainings')}>{t('public.footer.stats.trainings')}</div>
                </div>
                <div className="text-center group">
                  <div className="flex items-center justify-center w-8 h-8 bg-brand-primary/20 rounded-lg mb-2 mx-auto transition-transform duration-300 group-hover:scale-110 group-hover:bg-brand-primary/30">
                    <Building2 className="h-4 w-4 text-brand-primary" />
                  </div>
                  <div className="text-sm font-bold text-white">{stats ? `${stats.totalCenters}+` : '25+'}</div>
                  <div className="text-[10px] sm:text-xs text-gray-400 mt-1 line-clamp-1" title={t('public.footer.stats.centers')}>{t('public.footer.stats.centers')}</div>
                </div>
                <div className="text-center group">
                  <div className="flex items-center justify-center w-8 h-8 bg-brand-primary/20 rounded-lg mb-2 mx-auto transition-transform duration-300 group-hover:scale-110 group-hover:bg-brand-primary/30">
                    <Users className="h-4 w-4 text-brand-primary" />
                  </div>
                  <div className="text-sm font-bold text-white">{stats ? `${stats.totalTrainees}+` : '1000+'}</div>
                  <div className="text-[10px] sm:text-xs text-gray-400 mt-1 line-clamp-1" title={t('public.footer.stats.trainees', 'Trainees')}>{t('public.footer.stats.trainees', 'Trainees')}</div>
                </div>
              </div>
            </div>
          </div>


        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-black border-t border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-center text-sm text-gray-400">
            <div className="mb-2 sm:mb-0">
              © {currentYear} PHINLEX. {t('public.footer.copyright')}
            </div>
            <div className="text-xs text-gray-500">
              {t('public.footer.tagline')}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
